/**
 * Question types supported by the API
 * Each type requires specific analysis approaches and UI components
 */
export const QUESTION_TYPES = {
  OPEN_ENDED: 'OPEN_ENDED',
  BINARY: 'BINARY',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  MULTIPLE_RATING: 'MULTIPLE_RATING',
  SCALE: 'SCALE',
  SCALE_5: 'SCALE_5',
  SCALE_10: 'SCALE_10',
  STACK_RANK: 'STACK_RANK',
  MATRIX: 'MATRIX',
  RATING: 'RATING',
  RANKING: 'RANKING'
};

/**
 * Session types supported by the API
 * Each session type has specific focus areas and analysis requirements
 */
export const SESSION_TYPES = {
  CROWDSOURCING: 'crowdsourcing', // Open-ended feedback, innovation focus
  PRIORITIZE: 'prioritize',       // Ranking tasks, resource allocation
  SURVEY: 'survey'                // Structured feedback, metrics focus
};

/**
 * Analysis themes supported by the API
 * Each theme has specific metrics and indicators to analyze
 */
export const ANALYSIS_THEMES = {
  THEMES: 'themes',
  PATTERNS: 'patterns',
  RECOMMENDATIONS: 'recommendations',
  DISTRIBUTION: 'distribution',
  CORRELATIONS: 'correlations'
};

/**
 * Analysis metrics supported by the API
 * Used for quantifying and measuring different aspects of responses
 */
export const ANALYSIS_METRICS = {
  AVERAGE: 'average',
  PERCENTAGE: 'percentage',
  COUNT: 'count',
  RANK: 'rank'
};

/**
 * Keyword categories for sentiment analysis
 */
export const KEYWORD_CATEGORIES = {
  POSITIVE: {
    TEAM: ['team alignment', 'collaboration', 'synergy'],
    ENGAGEMENT: ['motivation', 'enthusiasm', 'commitment'],
    RECOGNITION: ['appreciation', 'acknowledgment', 'reward']
  },
  NEUTRAL: {
    PROCESS: ['workflow', 'procedure', 'system'],
    SENTIMENT: ['feedback', 'observation', 'perspective']
  },
  ACTIONABLE: {
    IMPROVEMENT: ['enhance', 'optimize', 'streamline'],
    OPTIMIZATION: ['efficiency', 'productivity', 'performance']
  }
};

/**
 * Summary view structure requirements
 */
export const SUMMARY_STRUCTURE = {
  OVERVIEW: 'session_overview',
  QUESTION_INSIGHTS: 'question_type_insights',
  THEME_ANALYSIS: 'theme_analysis',
  ACTION_ITEMS: 'action_items',
  CORRELATIONS: 'cross_event_correlations'
};

/**
 * Cache configuration
 */
export const CACHE_CONFIG = {
  PREFIX: 'ai_insights_',
  TTL: 20 * 60 * 1000, // 20 minutes in milliseconds
  INVALIDATION_EVENTS: ['event_update', 'post_create', 'post_update', 'post_delete']
};

export const API_ENDPOINTS = {
  INSIGHTS: '/ai/insights',
  QUESTION_TYPE: '/ai/question-type'
};

/**
 * Analysis requirements for different question types
 */
export const ANALYSIS_REQUIREMENTS = {
  OPEN_ENDED: {
    requiredMetrics: [
      ANALYSIS_METRICS.COUNT,
      ANALYSIS_METRICS.PERCENTAGE
    ],
    requiredThemes: [
      ANALYSIS_THEMES.THEMES,
      ANALYSIS_THEMES.PATTERNS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.NEUTRAL,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.THEME_ANALYSIS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  BINARY: {
    requiredMetrics: [
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.PATTERNS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  MULTIPLE_CHOICE: {
    requiredMetrics: [
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.CORRELATIONS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.NEUTRAL,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.THEME_ANALYSIS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  MULTIPLE_RATING: {
    requiredMetrics: [
      ANALYSIS_METRICS.AVERAGE,
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.CORRELATIONS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.NEUTRAL,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.THEME_ANALYSIS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  SCALE: {
    requiredMetrics: [
      ANALYSIS_METRICS.AVERAGE,
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.PATTERNS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.NEUTRAL,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  SCALE_5: {
    requiredMetrics: [
      ANALYSIS_METRICS.AVERAGE,
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.PATTERNS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.NEUTRAL,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  SCALE_10: {
    requiredMetrics: [
      ANALYSIS_METRICS.AVERAGE,
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.PATTERNS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.NEUTRAL,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  STACK_RANK: {
    requiredMetrics: [
      ANALYSIS_METRICS.RANK,
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.CORRELATIONS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.THEME_ANALYSIS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  MATRIX: {
    requiredMetrics: [
      ANALYSIS_METRICS.AVERAGE,
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.CORRELATIONS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.NEUTRAL,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.THEME_ANALYSIS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  RATING: {
    requiredMetrics: [
      ANALYSIS_METRICS.AVERAGE,
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.PATTERNS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.NEUTRAL,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  },
  RANKING: {
    requiredMetrics: [
      ANALYSIS_METRICS.RANK,
      ANALYSIS_METRICS.PERCENTAGE,
      ANALYSIS_METRICS.COUNT
    ],
    requiredThemes: [
      ANALYSIS_THEMES.DISTRIBUTION,
      ANALYSIS_THEMES.CORRELATIONS
    ],
    keywordCategories: [
      KEYWORD_CATEGORIES.POSITIVE,
      KEYWORD_CATEGORIES.ACTIONABLE
    ],
    summaryStructure: [
      SUMMARY_STRUCTURE.OVERVIEW,
      SUMMARY_STRUCTURE.QUESTION_INSIGHTS,
      SUMMARY_STRUCTURE.THEME_ANALYSIS,
      SUMMARY_STRUCTURE.ACTION_ITEMS
    ]
  }
};

export const REQUIRED_SECTIONS = {
  DEFAULT: ['executiveSummary', 'keyThemes', 'actionableInsights'],
  OPEN_ENDED: ['executiveSummary', 'keyThemes', 'actionableInsights', 'sentimentAnalysis'],
  BINARY: ['executiveSummary', 'keyThemes', 'actionableInsights', 'distributionAnalysis'],
  MULTIPLE_CHOICE: ['executiveSummary', 'keyThemes', 'actionableInsights', 'distributionAnalysis'],
  MULTIPLE_RATING: ['executiveSummary', 'keyThemes', 'actionableInsights', 'distributionAnalysis'],
  SCALE: ['executiveSummary', 'keyThemes', 'actionableInsights', 'distributionAnalysis'],
  SCALE_5: ['executiveSummary', 'keyThemes', 'actionableInsights', 'distributionAnalysis'],
  SCALE_10: ['executiveSummary', 'keyThemes', 'actionableInsights', 'distributionAnalysis'],
  STACK_RANK: ['executiveSummary', 'keyThemes', 'actionableInsights', 'priorityDistribution'],
  MATRIX: ['executiveSummary', 'keyThemes', 'actionableInsights', 'matrixAnalysis'],
  RATING: ['executiveSummary', 'keyThemes', 'actionableInsights', 'ratingDistribution'],
  RANKING: ['executiveSummary', 'keyThemes', 'actionableInsights', 'rankingAnalysis']
};
